import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeft,
  Package,
  Loader,
  AlertCircle,
  CheckCircle,
  ChevronRight,
  ChevronDown,
  X,
  RefreshCw,
  Truck
} from 'lucide-react';
import { packingService } from '../../../../services/packingService';
import { shipmentLineService } from '../../../../services/shipmentLineService';
import { settingsService } from '../../../../services/settingsService';
import { userProfileService } from '../../../../services/userProfileService';
import {
  ShipmentDetailDto,
  ShipmentOrderDto,
  OrderLineDto,
  UpdateShipmentRequest,
  DeliveryMethodDto,
  PackZoneDto,
  LoadingZoneDto
} from '../../../../types/packing';
import { ShipmentLineDto, ShipmentLineRow } from '../../../../types/shipmentLine';
import { ShipmentLineEditor } from '../../shared/ShipmentLineEditor';
import { PackageConfirmationSection } from './PackageConfirmationSection';
import { MediaCaptureButton } from '../../../common/media/MediaCaptureButton';
import { HelpHint } from '../../../common/HelpHint';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { NumericInput } from '../../../common/inputs';
import { useOperatorDisplayNames } from '../../../../hooks/useOperatorDisplayNames';
import { TaValidationDialog } from '../../../shared/TaValidationDialog';
import { TaValidationIssue } from '../../../../types/picking';
import { logger } from '../../../../services/loggingService';

interface PackingDetailViewProps {
  groupingId: string;
  onBack: () => void;
  onComplete?: () => void;
}

export const PackingDetailView: React.FC<PackingDetailViewProps> = ({
  groupingId,
  onBack,
  onComplete
}) => {
  const { t } = useTranslation();
  const { getDisplayName } = useOperatorDisplayNames();
  const [shipment, setShipment] = useState<ShipmentDetailDto | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // TA-forhåndsvalidering før avslutning (36369121): vises når nShift-valideringen
  // fant avvik. Header + kollilinjer er da allerede lagret — «Send likevel» går rett
  // til finalize, «Avbryt og rett» blir stående i pakkebildet.
  const [showTaValidation, setShowTaValidation] = useState(false);
  const [taValidationIssues, setTaValidationIssues] = useState<TaValidationIssue[]>([]);
  const [taValidationBusy, setTaValidationBusy] = useState(false);

  // Feilbanneret ligger øverst i skjermen — valideringsfeil fra handleFinalize (knappen nederst)
  // ville ellers vært usynlige for operatøren. Scroll banneret inn i viewport når feil settes.
  const errorRef = React.useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (error) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);
  // Composite key "orderNo-pListID" so that rows sharing the same orderNo
  // (an order split across multiple PLists) expand independently.
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [orderLines, setOrderLines] = useState<OrderLineDto[]>([]);
  const [loadingLines, setLoadingLines] = useState(false);

  // Dropdown data
  const [deliveryMethods, setDeliveryMethods] = useState<DeliveryMethodDto[]>([]);
  const [packZones, setPackZones] = useState<PackZoneDto[]>([]);
  const [loadingZones, setLoadingZones] = useState<LoadingZoneDto[]>([]);

  // Package confirmation state
  const [packageCheckoutRequired, setPackageCheckoutRequired] = useState(false);
  const [allPackagesConfirmed, setAllPackagesConfirmed] = useState(false);

  // Punkt 12: kolli/vekt-krav er PROFILSTYRT (bakoverkompatibelt — av som standard). Samme flagg
  // som plukkavslutning bruker (UserProfile). Lastes i loadDropdownData.
  const [pListNumOfPackagesRequired, setPListNumOfPackagesRequired] = useState(false);
  const [pListWeightRequired, setPListWeightRequired] = useState(false);
  const [pListNumOfPalletsRequired, setPListNumOfPalletsRequired] = useState(false);

  // Editable fields
  const [numOfPackages, setNumOfPackages] = useState<number>(0);
  // Antall paller er en STRENG-state: tomt felt = «ikke registrert» må kunne skilles fra
  // bevisst tastet 0 («ingen paller sendes» er gyldig). Kravsjekken i handleFinalize feiler
  // kun på tom streng.
  const [numOfPallets, setNumOfPallets] = useState<string>('');
  const [totalWeight, setTotalWeight] = useState<string>('');
  const [totalVolume, setTotalVolume] = useState<string>('');
  const [packageRemark, setPackageRemark] = useState<string>('');
  const [selectedDeliveryMethodNo, setSelectedDeliveryMethodNo] = useState<number | null>(null);
  const [selectedPackZoneID, setSelectedPackZoneID] = useState<string>('');
  const [selectedLoadingZoneID, setSelectedLoadingZoneID] = useState<number | null>(null);

  // DisplayText fra backend slutter med rå bruker-ID (AssignedTo, f.eks. «Lager2»).
  // Vis ProfileName i stedet (samme mønster som resten av UX-pakken) — bytt kun hale-forekomsten,
  // fallback til uendret tekst når profilen ikke finnes eller teksten ikke slutter med ID-en.
  const formatOrderText = useCallback((order: ShipmentOrderDto): string => {
    if (!order.assignedTo) return order.displayText;
    const name = getDisplayName(order.assignedTo);
    if (!name || name === order.assignedTo) return order.displayText;
    return order.displayText.endsWith(` ${order.assignedTo}`)
      ? order.displayText.slice(0, -order.assignedTo.length) + name
      : order.displayText;
  }, [getDisplayName]);

  // OrderNos that appear on more than one row (order split across multiple PLists).
  // Used to append "#PListID" suffix to displayText so operators can tell rows apart.
  const splitOrderNos = useMemo(() => {
    if (!shipment) return new Set<number>();
    const counts = new Map<number, number>();
    for (const o of shipment.orders) {
      counts.set(o.orderNo, (counts.get(o.orderNo) || 0) + 1);
    }
    return new Set(Array.from(counts.entries()).filter(([, n]) => n > 1).map(([no]) => no));
  }, [shipment]);

  // Punkt 15: ETT kollidetalj-sett pr. FORSENDELSE (ikke pr. ordre). Lastes/lagres via ShipmentID.
  const [shipmentLines, setShipmentLines] = useState<ShipmentLineDto[]>([]);
  const [shipmentLinesChanges, setShipmentLinesChanges] = useState<ShipmentLineRow[] | null>(null);
  const [shipmentLinesExpanded, setShipmentLinesExpanded] = useState(false);
  const [shipmentLinesLoaded, setShipmentLinesLoaded] = useState(false);
  const [loadingShipmentLines, setLoadingShipmentLines] = useState(false);

  useEffect(() => {
    loadShipment();
    loadDropdownData();
  }, [groupingId]);

  const loadDropdownData = async () => {
    try {
      const [methods, zones, lzones, checkoutRequired] = await Promise.all([
        packingService.getDeliveryMethods(),
        packingService.getPackZones(),
        packingService.getLoadingZones(),
        packingService.isPackageCheckoutRequired()
      ]);
      setDeliveryMethods(methods);
      setPackZones(zones);
      setLoadingZones(lzones);
      setPackageCheckoutRequired(checkoutRequired);
    } catch (err) {
      console.error('Error loading dropdown data:', err);
    }

    // Punkt 12: last profil-flagg separat så en profil-feil ikke knekker dropdowns.
    try {
      const profile = await userProfileService.getCurrentUserProfile();
      setPListNumOfPackagesRequired(profile?.pListNumOfPackagesRequired ?? false);
      setPListWeightRequired(profile?.pListWeightRequired ?? false);
      setPListNumOfPalletsRequired(profile?.pListNumOfPalletsRequired ?? false);
    } catch (err) {
      console.warn('Could not load user profile for required-field flags:', err);
    }
  };

  // Callback when all packages are confirmed
  const handleAllPackagesConfirmed = useCallback(() => {
    setAllPackagesConfirmed(true);
  }, []);

  // Punkt 10/12: per-leveringsmåte-flagg som krever at kollidetalj-LINJER (kollilinjer) er registrert
  // + vekt > 0 før avslutning. Avledet → re-evalueres når operatøren bytter leveringsmåte.
  // (Global packagecheckout-setting er en SEPARAT mekanisme for pakke-bekreftelse, se handleFinalize.)
  const selectedDeliveryMethod = deliveryMethods.find(m => m.deliveryMethodNo === selectedDeliveryMethodNo);
  const requirePackageDetails = selectedDeliveryMethod?.requirePackageDetailsYesNo ?? false;

  const loadShipment = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await packingService.getOrCreateShipment(groupingId);
      setShipment(data);

      // Initialize editable fields
      // Prefyll "Antall kolli" KUN fra settingen picking.defaultNumOfPackages; ikke satt → 0 (operatør bestemmer).
      // MERK: header-"antall kolli" er et konsolideringsfelt i pakk — det er IKKE antallet som sendes til TA.
      // Antall til TA bestemmes av kolli-linjene (sum av linje-antall) når de er registrert. Internkolli
      // (PListOrderPackages / distinkte SSCC) brukes kun til pakke-bekreftelse, ikke til prefyll her.
      let prefillPackages = 0;
      try {
        const np = await settingsService.getEffectiveSetting('picking.defaultNumOfPackages');
        const parsed = parseInt(np?.currentValue ?? '', 10);
        if (!isNaN(parsed) && parsed >= 0) prefillPackages = parsed;
      } catch {
        /* fallback 0 */
      }
      setNumOfPackages(prefillPackages);
      // Paller: DB kan ikke skille default-0 (aldri registrert) fra bevisst 0 → prefyll kun
      // reelle verdier (> 0); ellers tomt så pall-kravet (når på) tvinger et bevisst valg.
      setNumOfPallets(data.numOfPallets && data.numOfPallets > 0 ? data.numOfPallets.toString() : '');
      setTotalWeight(data.totalWeight?.toString() || '');
      setTotalVolume(data.totalVolume?.toString() || '');
      setPackageRemark(data.packageRemark || '');
      setSelectedDeliveryMethodNo(data.deliveryMethodNo);
      setSelectedPackZoneID(data.packZoneID || '');
      setSelectedLoadingZoneID(data.loadingZoneID ?? data.suggestedLoadingZoneID ?? null);
    } catch (err) {
      console.error('Error loading shipment:', err);
      setError(err instanceof Error ? err.message : t('warehouse.modules.packing.errors.loadShipmentFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!shipment) return;

    setSaving(true);
    try {
      const request: UpdateShipmentRequest = {
        shipmentID: shipment.shipmentID,
        numOfPackages,
        // Tomt felt lagres som 0 (samme lagrede verdi som før string-konverteringen).
        numOfPallets: numOfPallets !== '' ? parseFloat(numOfPallets) || 0 : 0,
        totalWeight: totalWeight ? parseFloat(totalWeight) : null,
        totalVolume: totalVolume ? parseFloat(totalVolume) : null,
        packageRemark: packageRemark || null,
        deliveryMethodNo: selectedDeliveryMethodNo,
        packZoneID: selectedPackZoneID || null,
        loadingZoneID: selectedLoadingZoneID
      };

      const response = await packingService.updateShipment(request);

      if (response.success) {
        // Refresh data
        await loadShipment();
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (!shipment) return;

    // Punkt 15: kollidetalj-linjer (ETT sett pr. forsendelse). Lazy-last fra backend hvis seksjonen ikke
    // er åpnet, så vi har dem til både krav- og avviks-sjekkene under.
    let detailLines: (ShipmentLineDto | ShipmentLineRow)[] = shipmentLinesChanges ?? shipmentLines;
    if (shipmentLinesChanges === null && !shipmentLinesLoaded) {
      try {
        detailLines = await shipmentLineService.getByShipmentId(shipment.shipmentID);
      } catch {
        detailLines = [];
      }
    }
    const hasDetailLines = detailLines.length > 0;

    // Punkt 10: når leveringsmåten krever kollidetaljer → kollidetalj-LINJER MÅ være registrert.
    if (requirePackageDetails && !hasDetailLines) {
      setError(t('warehouse.modules.packing.validation.packageDetailsRequired'));
      return;
    }

    // Punkt 12: kolli + vekt påkrevd (profilstyrt) — MEN bare når det IKKE er registrert kolli-linjer.
    // Er kolli-linjer registrert, er de gode nok som validering (samme regel som P11). Av som standard
    // → bakoverkompatibelt. (Header-feltene er en oppsummering; kolli-linjene er den autoritative kilden.)
    if (!hasDetailLines) {
      if (pListNumOfPackagesRequired && numOfPackages <= 0) {
        setError(t('warehouse.modules.packing.validation.packagesRequired'));
        return;
      }
      if (pListWeightRequired && !(parseFloat(totalWeight) > 0)) {
        setError(t('warehouse.modules.packing.validation.weightRequired'));
        return;
      }
    }

    // Kolli-linjer waiver header-vektkravet (P12 over), men da må HVER linje ha vekt — TA-godsradene
    // bygges per kollilinje, og NShift avviser forsendelser der en linje mangler vekt (soft-fail som
    // gir fullført forsendelse uten fraktbooking).
    if (hasDetailLines && pListWeightRequired && detailLines.some(l => !((l.weight ?? 0) > 0))) {
      setError(t('warehouse.modules.packing.validation.lineWeightRequired', {}, 'Vekt må fylles ut på alle kollilinjer før forsendelsen kan avsluttes'));
      return;
    }

    // Antall paller: waives ALDRI av kolli-linjer (ren ERP-/fakturerings-info) — samme regel
    // som direkte plukk-avslutning (DeliveryInfoModal). 0 er GYLDIG («ingen paller sendes»),
    // men må tastes bevisst: kun tomt felt blokkerer.
    if (pListNumOfPalletsRequired && numOfPallets === '') {
      setError(t('warehouse.modules.packing.validation.palletsRequired', {}, 'Antall paller er påkrevd (0 er gyldig når ingen paller sendes)'));
      return;
    }

    // Pakke-bekreftelse: SEPARAT global packagecheckout-setting (uendret oppførsel).
    if (packageCheckoutRequired && !allPackagesConfirmed) {
      setError(t('warehouse.modules.packing.validation.confirmAllPackages'));
      return;
    }

    // Punkt 11: advarsel (ikke blokk) hvis sum av kollidetaljer ikke stemmer med header-totalen.
    // Sjekkes KUN per header-felt som faktisk har en verdi — er kun kolli-linjer registrert (tomt
    // header-nivå), varsler vi ikke (kolli-linjene er gode nok).
    if (hasDetailLines) {
      const sumPackages = detailLines.reduce((s, l) => s + (l.numOfPackages || 0), 0);
      const sumWeight = detailLines.reduce((s, l) => s + (l.weight || 0) * (l.numOfPackages || 0), 0);
      const sumVolume = detailLines.reduce((s, l) => s + (l.volume || 0) * (l.numOfPackages || 0), 0);
      const headerPackages = numOfPackages;
      const headerWeight = parseFloat(totalWeight) || 0;
      const headerVolume = parseFloat(totalVolume) || 0;
      // Toleranse: antall kolli eksakt; vekt/volum tillatt liten epsilon (abs > 0.01 OG relativ > 1 %).
      const offBy = (a: number, b: number) => Math.abs(a - b) > 0.01 && Math.abs(a - b) / b > 0.01;
      const mismatch =
        (headerPackages > 0 && sumPackages !== headerPackages) ||
        (headerWeight > 0 && offBy(sumWeight, headerWeight)) ||
        (headerVolume > 0 && offBy(sumVolume, headerVolume));
      if (mismatch && !window.confirm(t('warehouse.modules.packing.validation.packageDetailsMismatch'))) {
        return;
      }
    }

    if (!window.confirm(t('warehouse.modules.packing.confirmFinalize'))) {
      return;
    }

    setSaving(true);
    try {
      // First save any pending changes
      await handleSave();

      // Punkt 15: Lagre kollidetaljene på FORSENDELSESNIVÅ (ett sett pr. forsendelse).
      if (shipmentLinesChanges !== null) {
        await shipmentLineService.saveAll({
          shipmentID: shipment.shipmentID,
          pListID: 0,
          orderNo: 0,
          lines: shipmentLinesChanges.map((line, index) => ({
            lineID: line.lineID || undefined,
            transitPackagingID: line.transitPackagingID,
            description1: line.description1,
            numOfPackages: line.numOfPackages,
            weight: line.weight,
            volume: line.volume,
            height: line.height,
            width: line.width,
            length: line.length,
            sorting: line.sorting ?? index
          }))
        });
      }

      // TA-forhåndsvalidering (36369121): kjøres ETTER at header + kollilinjer er lagret,
      // så payloaden (bygget fra DB) blir identisk med bookingens — én konsolidert
      // consignment per forsendelse. Gated bak ta.validateBeforeCompletion (backend
      // svarer applicable=false når av). Fail-open: nettverksfeil her skal aldri
      // blokkere avslutningen.
      try {
        const validation = await packingService.validateTaBeforeFinalize(shipment.shipmentID);
        if (validation.applicable && !validation.valid) {
          setTaValidationIssues(validation.issues || []);
          setShowTaValidation(true);
          return; // flyten fortsetter i handleTaValidationProceed / -Cancel
        }
      } catch (validationError) {
        console.warn('TA-validering utilgjengelig — fortsetter uten (fail open):', validationError);
      }

      await finalizeNow();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('warehouse.modules.packing.errors.finalizeFailed'));
    } finally {
      setSaving(false);
    }
  };

  /** Selve avslutningen — delt mellom normal flyt og «Send likevel»-valget i TA-dialogen. */
  const finalizeNow = async () => {
    if (!shipment) return;
    const response = await packingService.finalizeShipment(shipment.shipmentID);
    if (response.success) {
      onComplete?.();
      onBack();
    } else {
      setError(response.message);
    }
  };

  /** «Avbryt og rett» — bli stående i pakkebildet; header/kollilinjer er allerede lagret. */
  const handleTaValidationCancel = () => {
    setShowTaValidation(false);
  };

  /** «Send likevel, og rett i nShift» — fullfør; overstyringen logges sporbart. */
  const handleTaValidationProceed = async () => {
    if (!shipment) return;
    setTaValidationBusy(true);
    try {
      // Sporbarhet: hvem overstyrte hvilke avvik (samme mønster som plukk-direkteflyten).
      logger.logUserAction('ta_validation_override', {
        shipmentID: shipment.shipmentID,
        issues: taValidationIssues.map(i => i.message)
      });
      setShowTaValidation(false);
      await finalizeNow();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('warehouse.modules.packing.errors.finalizeFailed'));
    } finally {
      setTaValidationBusy(false);
    }
  };

  const handleOrderClick = async (order: ShipmentOrderDto) => {
    const key = `${order.orderNo}-${order.pListID}`;
    if (expandedOrder === key) {
      setExpandedOrder(null);
      setOrderLines([]);
      return;
    }

    setExpandedOrder(key);
    setLoadingLines(true);

    try {
      const lines = await packingService.getOrderLines(order.orderNo, order.pListID);
      setOrderLines(lines);
    } catch (err) {
      console.error('Error loading order lines:', err);
    } finally {
      setLoadingLines(false);
    }
  };

  // Punkt 15: Toggle kollidetalj-seksjonen på FORSENDELSESNIVÅ (ett sett pr. forsendelse).
  const handleShipmentLinesToggle = async () => {
    if (shipmentLinesExpanded) {
      setShipmentLinesExpanded(false);
      return;
    }

    setShipmentLinesExpanded(true);

    if (!shipmentLinesLoaded && shipment) {
      setLoadingShipmentLines(true);
      try {
        const lines = await shipmentLineService.getByShipmentId(shipment.shipmentID);
        setShipmentLines(lines);
        setShipmentLinesLoaded(true);
      } catch (err) {
        console.error('Error loading shipment lines:', err);
      } finally {
        setLoadingShipmentLines(false);
      }
    }
  };

  // Punkt 15: Endringer i kollidetaljene (ett sett pr. forsendelse).
  const handleShipmentLinesChange = (lines: ShipmentLineRow[]) => {
    setShipmentLinesChanges(lines);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-2" />
          <p className="text-sm sm:text-base text-gray-600">{t('warehouse.modules.packing.loadingShipment')}</p>
        </div>
      </div>
    );
  }

  if (error && !shipment) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
          <div className="flex items-start gap-2 sm:gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-red-900">{t('common.error')}</h3>
              <p className="text-sm text-red-700 break-words">{error}</p>
              <button
                onClick={onBack}
                className="mt-3 text-sm text-red-600 hover:text-red-700 min-h-[44px] inline-flex items-center"
              >
                {t('common.buttons.back')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!shipment) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center gap-2 min-w-0">
              <Truck className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <span className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                {t('warehouse.modules.packing.shipmentOrder')}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <MediaCaptureButton
              entityType="Packing"
              entityKey={groupingId}
            />
            <button
              onClick={loadShipment}
              className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <RefreshCw className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div ref={errorRef} className="mx-3 sm:mx-4 mt-3 sm:mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 flex-1 break-words">{error}</p>
            <button
              onClick={() => setError(null)}
              className="flex-shrink-0 min-h-[44px] min-w-[44px] -m-2 p-2 flex items-center justify-center hover:bg-red-100 rounded"
            >
              <X className="w-4 h-4 text-red-600" />
            </button>
          </div>
        </div>
      )}

      {/* D-varsel: plukk i samme gruppe som IKKE er med i denne forsendelsen */}
      {shipment && (shipment.unincludedPickCount ?? 0) > 0 && (
        <div className="mx-3 sm:mx-4 mt-3 sm:mt-4 bg-amber-50 border border-amber-300 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800 flex-1">
              <p className="font-medium">
                {t('warehouse.modules.packing.unincludedPicks.title', {}, 'Ikke alle plukk i denne gruppen er med i forsendelsen')}
              </p>
              <p className="mt-0.5">
                {t('warehouse.modules.packing.unincludedPicks.body', { '0': shipment.unincludedPickCount ?? 0 },
                  `${shipment.unincludedPickCount} plukk er ikke med i denne forsendelsen. Sjekk at de skal sendes separat før du avslutter.`)}
                {shipment.unincludedPickOrderNos ? ` (${shipment.unincludedPickOrderNos})` : ''}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Form Fields */}
      <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
        {/* Row 1: Antall kolli, Antall paller, Vekt, Volum, Merkes */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('warehouse.modules.packing.fields.numberOfPackages')}</label>
            <NumericInput
              mode="integer"
              value={numOfPackages}
              onChange={(e) => setNumOfPackages(parseInt(e.target.value) || 0)}
              className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
            />
          </div>
          <div>
            {/* Tablet-vennlig: lite trykkbart (?) som viser hjelpeteksten ved trykk (ikke hover). */}
            <label className="flex items-center gap-1 text-xs text-gray-500 mb-1">
              <span>{t('warehouse.modules.packing.fields.numberOfPallets')}</span>
              <HelpHint
                label={t('warehouse.modules.packing.fields.numberOfPallets')}
                text={t('warehouse.modules.packing.fields.numberOfPalletsTooltip', {}, 'Brukes kun for ERP-rapportering og fakturering (blir vanligvis ordretilleggslinjer). Sendes ikke til transportøren (TA).')}
              />
            </label>
            <NumericInput
              mode="decimal"
              value={numOfPallets}
              onChange={(e) => setNumOfPallets(e.target.value)}
              className={`w-full px-2 sm:px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px] ${
                pListNumOfPalletsRequired && numOfPallets === '' ? 'border-red-300' : 'border-gray-300'
              }`}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('warehouse.modules.packing.fields.weight')}</label>
            <input
              type="text"
              value={totalWeight}
              onChange={(e) => setTotalWeight(e.target.value)}
              className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('warehouse.modules.packing.fields.volume')}</label>
            <input
              type="text"
              value={totalVolume}
              onChange={(e) => setTotalVolume(e.target.value)}
              className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
            />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-xs text-gray-500 mb-1">{t('warehouse.modules.packing.fields.remark')}</label>
            <input
              type="text"
              value={packageRemark}
              onChange={(e) => setPackageRemark(e.target.value)}
              className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
            />
          </div>
        </div>

        {/* Row 2: Transportør, Lastesone, Dato, Navn */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('warehouse.modules.packing.fields.carrier')}</label>
            <select
              className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
              value={selectedDeliveryMethodNo ?? ''}
              onChange={(e) => setSelectedDeliveryMethodNo(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">{t('common.select')}</option>
              {deliveryMethods.map((method) => (
                <option key={method.deliveryMethodNo} value={method.deliveryMethodNo}>
                  {method.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('warehouse.modules.packing.fields.packZone')}</label>
            <select
              className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
              value={selectedLoadingZoneID ?? ''}
              onChange={(e) => setSelectedLoadingZoneID(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">{t('common.select')}</option>
              {loadingZones.map((zone) => (
                <option key={zone.loadingZoneID} value={zone.loadingZoneID}>
                  {zone.description}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('common.fields.date')}</label>
            <input
              type="text"
              value={shipment.shipmentDate ? new Date(shipment.shipmentDate).toLocaleDateString('nb-NO') : ''}
              readOnly
              className="w-full px-2 sm:px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 min-h-[44px]"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('common.fields.name')}</label>
            <input
              type="text"
              value={shipment.delCustomerName || ''}
              readOnly
              className="w-full px-2 sm:px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 min-h-[44px]"
            />
          </div>
        </div>

        {/* Row 3: Adresse, Postnr, Poststed, Landkode */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
          <div className="sm:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">{t('common.fields.address')}</label>
            <input
              type="text"
              value={shipment.delAddress1 || ''}
              readOnly
              className="w-full px-2 sm:px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 min-h-[44px]"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('common.fields.zipCode')}</label>
            <input
              type="text"
              value={shipment.delZipCode || ''}
              readOnly
              className="w-full px-2 sm:px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 min-h-[44px]"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('common.fields.city')}</label>
            <input
              type="text"
              value={shipment.delCity || ''}
              readOnly
              className="w-full px-2 sm:px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 min-h-[44px]"
            />
          </div>
        </div>

        {/* Package Confirmation Section */}
        <PackageConfirmationSection
          groupingId={groupingId}
          shipmentId={shipment.shipmentID}
          onAllPackagesConfirmed={handleAllPackagesConfirmed}
          isRequired={packageCheckoutRequired}
        />

        {/* Order List */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {shipment.orders.length === 0 ? (
            <div className="p-6 sm:p-8 text-center text-gray-500">
              <Package className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm">{t('warehouse.modules.packing.noOrdersInShipment')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {shipment.orders.map((order) => (
                <div key={`${order.orderNo}-${order.pListID}`}>
                  {/* Order row */}
                  <div className="flex items-center px-3 sm:px-4 py-3 hover:bg-gray-50 gap-2">
                    {/* Checkmark */}
                    <div className="w-6 sm:w-8 flex-shrink-0">
                      {order.isReadyForPacking && (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      )}
                    </div>

                    {/* Order info */}
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {formatOrderText(order)}
                        {splitOrderNos.has(order.orderNo) && (
                          <span className="ml-1 text-gray-400 font-normal">#{order.pListID}</span>
                        )}
                      </p>
                      {order.packZoneID && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap">
                          {t('warehouse.modules.packing.fields.packZoneLabel')}: {packZones.find(z => z.packZoneID === order.packZoneID)?.description || order.packZoneID}
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => {/* TODO: Remove from shipment */}}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleOrderClick(order)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
                      >
                        <ChevronRight
                          className={`w-5 h-5 transition-transform ${
                            expandedOrder === `${order.orderNo}-${order.pListID}` ? 'rotate-90' : ''
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Expanded order lines */}
                  {expandedOrder === `${order.orderNo}-${order.pListID}` && (
                    <div className="bg-gray-50 px-3 sm:px-4 py-3 border-t border-gray-200">
                      {loadingLines ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader className="w-5 h-5 text-blue-600 animate-spin" />
                        </div>
                      ) : orderLines.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">
                          {t('warehouse.modules.packing.noLinesFound')}
                        </p>
                      ) : (
                        <div className="overflow-x-auto -mx-3 sm:-mx-4 px-3 sm:px-4">
                          <table className="w-full text-sm min-w-[500px]">
                            <thead>
                              <tr className="text-xs text-gray-500 uppercase">
                                <th className="text-left py-1 px-1">{t('common.fields.itemNumber')}</th>
                                <th className="text-left py-1 px-1">{t('common.fields.description')}</th>
                                <th className="text-right py-1 px-1">{t('common.fields.quantity')}</th>
                                <th className="text-right py-1 px-1">{t('warehouse.modules.packing.fields.picked')}</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {orderLines.map((line) => (
                                <tr key={line.lineNo}>
                                  <td className="py-2 px-1 font-medium text-xs sm:text-sm">{line.itemNo}</td>
                                  <td className="py-2 px-1 text-gray-600 text-xs sm:text-sm">{line.itemDescription}</td>
                                  <td className="py-2 px-1 text-right text-xs sm:text-sm">{line.quantity}</td>
                                  <td className="py-2 px-1 text-right text-xs sm:text-sm">{line.quantityPicked}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Punkt 15: kollidetaljer flyttet til forsendelsesnivå — se egen seksjon under ordrelisten */}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Punkt 15: Transport-detaljer (kollidetaljer) — ETT sett pr. forsendelse */}
        {shipment && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={handleShipmentLinesToggle}
              className="w-full px-3 sm:px-4 py-3 flex items-center justify-between text-sm text-gray-700 hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-gray-500" />
                <span className="font-medium">{t('warehouse.modules.packing.packageDetails')}</span>
                {shipmentLines.length > 0 && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    {shipmentLines.length}
                  </span>
                )}
              </div>
              {loadingShipmentLines ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : shipmentLinesExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>

            {shipmentLinesExpanded && (
              <div className="px-3 sm:px-4 pb-3">
                <ShipmentLineEditor
                  pListId={0}
                  orderNo={0}
                  initialLines={shipmentLines}
                  onChange={handleShipmentLinesChange}
                  disabled={saving}
                  expanded={true}
                />
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-3 sm:pt-4">
          <button
            onClick={onBack}
            className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors min-h-[44px] order-2 sm:order-1"
          >
            {t('common.buttons.back')}
          </button>
          <button
            onClick={handleFinalize}
            disabled={saving}
            className="flex-1 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center justify-center gap-2 min-h-[44px] order-1 sm:order-2"
          >
            {saving ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                <span>{t('common.saving')}</span>
              </>
            ) : (
              <span>{t('warehouse.modules.packing.finalizeShipment')}</span>
            )}
          </button>
        </div>
      </div>

      {/* TA-forhåndsvalidering (36369121): avvik fra nShift vises FØR booking,
          med valget «Avbryt og rett» / «Send likevel, og rett i nShift» */}
      <TaValidationDialog
        isOpen={showTaValidation}
        issues={taValidationIssues}
        busy={taValidationBusy}
        onCancel={handleTaValidationCancel}
        onProceed={handleTaValidationProceed}
      />
    </div>
  );
};
